import React, { memo } from 'react';
import { Box, Button, Skeleton, TablePagination, Typography } from '@mui/material';
import { Add as AddIcon, UploadFileOutlined as UploadIcon, QuizOutlined as QuizIcon } from '@mui/icons-material';
import QuestionCard from './QuestionCard';
import QuestionFilters from './QuestionFilters';
import EmptyState from '../common/EmptyState';
import { Chapter, Question, PaginationState, GetImportanceColorFn, SearchScope } from './types';

interface QuestionPaneProps {
  chapter: Chapter;
  questions: Question[];
  pagination: PaginationState;
  searchQuery: string;
  filterType: string;
  filterImportance: string;
  loading: boolean;
  searching: boolean;
  searchScope: SearchScope;
  onSearchScopeChange: (scope: SearchScope) => void;
  canWrite: boolean;
  onAddQuestion: (chapter: Chapter) => void;
  onUploadCSV: (chapter: Chapter) => void;
  onSearchChange: (chapterId: string, value: string) => void;
  onTypeChange: (chapterId: string, value: string) => void;
  onImportanceChange: (chapterId: string, value: string) => void;
  onClearFilters: (chapterId: string) => void;
  onViewQuestion: (question: Question) => void;
  onEditQuestion: (question: Question) => void;
  onDeleteQuestion: (questionId: string) => void;
  onPageChange: (chapterId: string, page: number) => void;
  onRowsPerPageChange: (chapterId: string, limit: number) => void;
  getImportanceColor: GetImportanceColorFn;
}

const ROWS_PER_PAGE = [5, 10, 25, 50];

/** The selected chapter's questions. Scrolls with the page; the chapter list stays pinned beside it. */
const QuestionPane = memo<QuestionPaneProps>(({
  chapter,
  questions,
  pagination,
  searchQuery,
  filterType,
  filterImportance,
  loading,
  searching,
  searchScope,
  onSearchScopeChange,
  canWrite,
  onAddQuestion,
  onUploadCSV,
  onSearchChange,
  onTypeChange,
  onImportanceChange,
  onClearFilters,
  onViewQuestion,
  onEditQuestion,
  onDeleteQuestion,
  onPageChange,
  onRowsPerPageChange,
  getImportanceColor,
}) => {
  const filtered = !!(searchQuery || filterType || filterImportance);
  // Results can come from any chapter, so each card names its own.
  const acrossBank = searchScope === 'bank' && !!searchQuery.trim();
  const total = pagination.total || 0;

  return (
    <Box sx={{ minWidth: 0 }}>
      {/* Chapter heading */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Chapter {chapter.order}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{chapter.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? ' ' : `${total} question${total === 1 ? '' : 's'}${filtered ? (total === 1 ? ' matches' : ' match') : ''}${acrossBank ? ' across all chapters' : ''}`}
            {chapter.description ? ` · ${chapter.description}` : ''}
          </Typography>
        </Box>
        {canWrite && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button variant="outlined" size="small" startIcon={<UploadIcon />} onClick={() => onUploadCSV(chapter)} sx={{ textTransform: 'none', borderRadius: '10px' }}>
              Import CSV
            </Button>
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => onAddQuestion(chapter)} sx={{ textTransform: 'none', borderRadius: '10px' }}>
              Add question
            </Button>
          </Box>
        )}
      </Box>

      <QuestionFilters
        chapterId={chapter._id}
        searchQuery={searchQuery}
        filterType={filterType}
        filterImportance={filterImportance}
        searching={searching}
        searchScope={searchScope}
        onSearchScopeChange={onSearchScopeChange}
        onSearchChange={onSearchChange}
        onTypeChange={onTypeChange}
        onImportanceChange={onImportanceChange}
        onClearFilters={onClearFilters}
      />

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: '12px' }} />)}
        </Box>
      ) : questions.length === 0 ? (
        <EmptyState
          icon={<QuizIcon />}
          title={filtered ? 'No questions match' : 'No questions in this chapter yet'}
          description={filtered
            ? (searchQuery.trim() && !acrossBank
              ? 'Nothing in this chapter. Try searching all chapters, or check the spelling.'
              : 'Try fewer or different words, or clear the filters.')
            : 'Write questions one by one, or import a whole set from a CSV file.'}
          action={filtered
            ? (searchQuery.trim() && !acrossBank
              ? { label: 'Search all chapters', onClick: () => onSearchScopeChange('bank') }
              : { label: 'Clear filters', onClick: () => onClearFilters(chapter._id) })
            : canWrite ? { label: 'Add question', icon: <AddIcon />, onClick: () => onAddQuestion(chapter) } : undefined}
          secondaryAction={!filtered && canWrite ? { label: 'Import CSV', onClick: () => onUploadCSV(chapter) } : undefined}
          sx={{ py: { xs: 6, md: 8 } }}
        />
      ) : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {questions.map((question, index) => (
              <QuestionCard
                key={question._id}
                question={question}
                index={index}
                pagination={pagination}
                canWrite={canWrite}
                onView={onViewQuestion}
                onEdit={onEditQuestion}
                onDelete={onDeleteQuestion}
                getImportanceColor={getImportanceColor}
                showChapter={acrossBank}
              />
            ))}
          </Box>
          {total > ROWS_PER_PAGE[0] && (
            <TablePagination
              component="div"
              count={total}
              page={(pagination.page || 1) - 1}
              onPageChange={(_, page) => onPageChange(chapter._id, page + 1)}
              rowsPerPage={pagination.limit || 10}
              onRowsPerPageChange={(e) => onRowsPerPageChange(chapter._id, parseInt(e.target.value, 10))}
              rowsPerPageOptions={ROWS_PER_PAGE}
              sx={{ mt: 1 }}
            />
          )}
        </>
      )}
    </Box>
  );
});

QuestionPane.displayName = 'QuestionPane';

export default QuestionPane;
